"use client";

import Script from "next/script";
import { useEffect, useState, useRef } from "react";
import { useAccount, useCurrentUser } from "@zapier/client";
import { useSprigIdentity } from "./useSprigIdentity";
import "./types";

const SPRIG_ENVIRONMENT_ID = process.env.NEXT_PUBLIC_SPRIG_ENVIRONMENT_ID;

function initializeSprigShim(environmentId: string) {
  if (globalThis.window === undefined || globalThis.Sprig !== undefined) return;

  const Sprig = function (...args: unknown[]) {
    Sprig._queue.push(args);
  } as Window["Sprig"];

  Sprig.appId = environmentId;
  Sprig._queue = [];
  globalThis.Sprig = Sprig;
}

export function SprigLoader() {
  const { user, isLoading: userIsLoading } = useCurrentUser();
  const { account, isLoading: accountIsLoading } = useAccount();
  const [isReady, setIsReady] = useState(false);
  // Reference prevents double-initialization across re-renders (idempotency guard, not UI state)
  const hasInitializedReference = useRef(false);

  const userId = user?.id;
  const isAuthenticated = !userIsLoading && !accountIsLoading && !!userId;

  useEffect(() => {
    if (!SPRIG_ENVIRONMENT_ID) {
      console.warn("Sprig: NEXT_PUBLIC_SPRIG_ENVIRONMENT_ID is not set");
      return;
    }

    if (hasInitializedReference.current) return;

    if (isAuthenticated && userId) {
      // Initialize shim and pre-queue setUserId BEFORE the SDK script loads.
      // This prevents anonymous-first double counting of Monthly Unique Users.
      // See: Editor team's Sprig learnings - "Identify ASAP" pattern.
      initializeSprigShim(SPRIG_ENVIRONMENT_ID);
      globalThis.Sprig("setUserId", String(userId));
      hasInitializedReference.current = true;
      setIsReady(true);
    }
  }, [isAuthenticated, userId]);

  useSprigIdentity({ enabled: isReady, user, account });

  if (!isReady || !SPRIG_ENVIRONMENT_ID) {
    // eslint-disable-next-line unicorn/no-null -- React components return null to render nothing
    return null;
  }

  // Load the actual Sprig SDK from CDN.
  // We preserve the existing shim's _queue (which has pre-queued setUserId).
  // Sanitize environment ID to prevent script injection from malformed env vars.
  const safeEnvironmentId = SPRIG_ENVIRONMENT_ID.replaceAll(
    /[^a-zA-Z0-9]/g,
    "",
  );
  const sprigScript = `
    (function(l,e,a,p) {
      var S = window.Sprig;
      if (S && S._loaded) return;
      if (!S) {
        S = window.Sprig = function(){S._queue.push(arguments)};
        S._queue = [];
      }
      S.appId = a;
      S._loaded = true;
      window.UserLeap = S;
      a=l.createElement('script');
      a.async=1;a.src=e+'?id='+S.appId;
      p=l.getElementsByTagName('script')[0];
      p.parentNode.insertBefore(a, p);
    })(document, 'https://cdn.sprig.com/shim.js', '${safeEnvironmentId}');
  `;

  return (
    <Script
      id="sprig-sdk"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{ __html: sprigScript }}
    />
  );
}
